import React from 'react'
import { FormikProps } from 'formik'
import { ModalBody, ModalFooter } from 'uiComponents/popups/modal.new'
import ActionButton from 'uiComponents/buttons'
import classNames from 'classnames'
import { PricingPlanFormValues, TWO_YEAR_TERM } from '../../pricingPlanFormSchema'
import { RadioOptionTile } from 'uiComponents/optionTile'
import { useCreateFormatCurrencyString } from 'uiComponents/money/useCurrency'
import './renewal.scss'

interface PricingPlanRenewalProps {
    formik: FormikProps<PricingPlanFormValues>
    visible: boolean
    onBackClick: () => void
    onSubmit: () => void
}

export const PricingPlanRenewal = ({ formik, visible, onBackClick, onSubmit }: PricingPlanRenewalProps) => {
    const formatCurrency = useCreateFormatCurrencyString()
    const {
        minimumTerm,
        allowYearlyInstallments,
        allowMonthlyInstallments,
        totalPriceYearly,
        totalPriceMonthly,
        pricePerYear,
        priceUpfront,
    } = formik.values

    const isTwoYearTerm = minimumTerm === TWO_YEAR_TERM
    const contractLengthLabel = isTwoYearTerm ? '2-year' : '12-month'

    const annualPrice = isTwoYearTerm ? pricePerYear ?? (priceUpfront != null ? priceUpfront / 2 : null) : totalPriceYearly

    const monthlyFromAnnual = annualPrice ? formatCurrency((annualPrice / 12).toFixed(2)) : null
    const monthlyFromMonthly = totalPriceMonthly ? formatCurrency((totalPriceMonthly / 12).toFixed(2)) : null

    const renewIntoSameDescription = (
        <>
            {allowYearlyInstallments && (
                <span>Annual members continue at {formatCurrency(totalPriceYearly)} per year. </span>
            )}
            {allowMonthlyInstallments && <span>Monthly members continue at {monthlyFromMonthly} per month.</span>}
        </>
    )

    const renewIntoMonthlyDescription = (
        <>
            {allowYearlyInstallments && <span>Annual members switch to {monthlyFromAnnual} per month. </span>}
            {allowMonthlyInstallments && <span>Monthly members continue at {monthlyFromMonthly} per month.</span>}
        </>
    )

    const renewIntoOneYearDescription = annualPrice ? (
        <span>After the 2-year contract, members continue at {formatCurrency(annualPrice)} per year.</span>
    ) : (
        <span>After the 2-year contract, the membership continues year by year.</span>
    )

    const doNotRenewDescription = isTwoYearTerm
        ? 'The membership expires after the 2-year contract. Members will have to purchase a new one through the ticket shop.'
        : 'The membership expires after the first year. Members will have to purchase a new one through the ticket shop.'

    return (
        <div className={classNames('form-step pricing-plan-renewal', { visible })}>
            <ModalBody>
                <div className="renewal-options">
                    <RadioOptionTile
                        title="Do not renew"
                        description={doNotRenewDescription}
                        checked={!formik.values.renewAutomatically}
                        onClick={() => {
                            formik.setFieldValue('renewAutomatically', false)
                            formik.setFieldValue('renewIntoMonthly', false)
                        }}
                    />
                    {isTwoYearTerm ? (
                        <RadioOptionTile
                            title="Renew into a 1-year plan"
                            description={renewIntoOneYearDescription}
                            checked={formik.values.renewAutomatically}
                            onClick={() => {
                                formik.setFieldValue('renewAutomatically', true)
                                formik.setFieldValue('renewIntoMonthly', false)
                            }}
                        />
                    ) : (
                        <>
                            <RadioOptionTile
                                title="Renew into same plan"
                                description={renewIntoSameDescription}
                                checked={formik.values.renewAutomatically && !formik.values.renewIntoMonthly}
                                onClick={() => {
                                    formik.setFieldValue('renewAutomatically', true)
                                    formik.setFieldValue('renewIntoMonthly', false)
                                }}
                            />
                            {allowYearlyInstallments && (
                                <RadioOptionTile
                                    title="Renew into monthly"
                                    description={renewIntoMonthlyDescription}
                                    checked={formik.values.renewAutomatically && formik.values.renewIntoMonthly}
                                    onClick={() => {
                                        formik.setFieldValue('renewAutomatically', true)
                                        formik.setFieldValue('renewIntoMonthly', true)
                                    }}
                                />
                            )}
                        </>
                    )}
                </div>
                <div className="explanation">
                    These settings apply to the first renewal after the initial {contractLengthLabel} contract. Members
                    on a renewed plan continue indefinitely.
                    {isTwoYearTerm && ' After the 2-year contract ends, members can cancel monthly.'}
                </div>
            </ModalBody>
            <ModalFooter>
                <ActionButton kind="settings" secondary size="large" onClick={onBackClick}>
                    Back
                </ActionButton>
                <ActionButton size="large" onClick={onSubmit}>
                    Save
                </ActionButton>
            </ModalFooter>
        </div>
    )
}
